import { useCallback, useEffect, useState } from 'react';
import tableAPI from '../../api/tableAPI';

const parseError = (error, fallbackMessage) => {
  if (typeof error === 'string') return error;
  return error?.response?.data?.message || error?.message || fallbackMessage;
};

export default function useTables(initialStoreId = '') {
  const [tables, setTables] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(initialStoreId || '');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fetchStores = useCallback(async () => {
    try {
      const storeList = await tableAPI.getUserStores();
      setStores(storeList);

      if (!initialStoreId && !selectedStoreId && storeList.length === 1) {
        setSelectedStoreId(storeList[0]?._id || '');
      }
    } catch (fetchError) {
      setError(parseError(fetchError, 'Failed to load stores'));
    }
  }, [initialStoreId, selectedStoreId]);

  const fetchTables = useCallback(
    async (storeId = selectedStoreId) => {
      try {
        setLoading(true);
        setError('');
        const tableList = await tableAPI.getTables(storeId || '');
        setTables(Array.isArray(tableList) ? tableList : []);
      } catch (fetchError) {
        setTables([]);
        setError(parseError(fetchError, 'Failed to load tables'));
      } finally {
        setLoading(false);
      }
    },
    [selectedStoreId]
  );

  const runAction = useCallback(
    async (action, successText) => {
      try {
        setActionLoading(true);
        setError('');
        setSuccessMessage('');
        const result = await action();
        await fetchTables();
        if (successText) {
          setSuccessMessage(successText);
        }
        return result;
      } catch (actionError) {
        setError(parseError(actionError, 'Table action failed'));
        throw actionError;
      } finally {
        setActionLoading(false);
      }
    },
    [fetchTables]
  );

  const createTable = useCallback(
    async (newTable) => runAction(() => tableAPI.createTable(newTable), 'Table created successfully.'),
    [runAction]
  );

  const updateTable = useCallback(
    async (tableId, updates) => runAction(() => tableAPI.updateTable(tableId, updates), 'Table updated successfully.'),
    [runAction]
  );

  const deleteTable = useCallback(
    async (tableId) => runAction(() => tableAPI.deleteTable(tableId), 'Table deleted successfully.'),
    [runAction]
  );

  const mergeTables = useCallback(
    async (payload) => runAction(() => tableAPI.mergeTables(payload), 'Tables merged successfully.'),
    [runAction]
  );

  const splitTable = useCallback(
    async (payload) => runAction(() => tableAPI.splitTable(payload), 'Table split successfully.'),
    [runAction]
  );

  const moveTable = useCallback(
    async (payload) => runAction(() => tableAPI.moveTable(payload), 'Table moved successfully.'),
    [runAction]
  );

  const bulkUpdate = useCallback(
    async (updatedTables) => runAction(() => tableAPI.bulkUpdateTables(updatedTables), 'Layout updated successfully.'),
    [runAction]
  );

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  useEffect(() => {
    fetchTables(selectedStoreId);
  }, [fetchTables, selectedStoreId]);

  return {
    tables,
    setTables,
    stores,
    selectedStoreId,
    setSelectedStoreId,
    loading,
    actionLoading,
    error,
    setError,
    successMessage,
    setSuccessMessage,
    fetchStores,
    fetchTables,
    createTable,
    updateTable,
    deleteTable,
    mergeTables,
    splitTable,
    moveTable,
    bulkUpdate,
  };
}
